import React, { useEffect, useState } from 'react';
import { IAppComponentProps } from 'src/components';
import { Button, ConfigProvider, message, Select, Spin, Tabs, Typography, Space } from 'antd';
import { useIntl } from 'react-intl';
import enUS from 'antd/lib/locale/en_US';
import deDe from 'antd/lib/locale/de_DE';
import { saveAs } from 'save-as';
import { PageContainer } from '@ant-design/pro-layout';
import { getI18n } from '../../utils/Utils';
import { DownloadOutlined, FilePdfOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { getUserGuides_getUserGuidesDocList } from './__generated__/getUserGuides';
import { downloadUserGuideByBlobId, getUserGuidesDocs } from './HelpUserGuideService';
import { Buffer } from 'buffer';
import { getCurrentLocale } from '../../utils';
import { useAppSelector } from '../../main/hooks';

const { Title, Text } = Typography;

interface UserGuideDetail {
    language: string;
    userGuideBlob: number;
    __typename: string;
}

type UserGuideItemProps = {
    el: getUserGuides_getUserGuidesDocList;
    isLoading: (loading: boolean) => void;
} & IAppComponentProps;

const getDetaultLanguage = (details: UserGuideDetail[]): string => {
    const currentLocale = getCurrentLocale().toUpperCase();
    const detail =
        details.find((detail) => detail.language.toUpperCase() === currentLocale);
    if (detail) {
        return detail.language;
    }
    return details[0].language;
};

const UserGuideItem: React.FunctionComponent<UserGuideItemProps> = ({ el, intl, isLoading}: UserGuideItemProps) => {
    const [selectedLanguage, setSelectedLanguage] = useState<string>(getDetaultLanguage(el.details as unknown as UserGuideDetail[]));
    const [hovered, setHovered] = useState(false);
    const { rerenderAfterLanguageChange } = useAppSelector((state) => state.globalStateReducer);

    useEffect(() => {
        setSelectedLanguage(getDetaultLanguage(el.details as unknown as UserGuideDetail[]));
    }, [rerenderAfterLanguageChange]);

    const handleDownload = (userGuideDetails: UserGuideDetail[]) => {
        const blobId = userGuideDetails.find((detail) => detail.language.toUpperCase() === selectedLanguage.toUpperCase())?.userGuideBlob;
        isLoading(true);

        Promise.resolve(downloadUserGuideByBlobId(blobId))
            .then((data) => {
                const mimeType = data.downloadUserGuideDocument.value.mimeType;
                const decodedData = Buffer.from(data.downloadUserGuideDocument.value.encodedFileData, 'base64');
                const blob = new Blob([decodedData], { type: mimeType });
                saveAs(blob, data.downloadUserGuideDocument.value.fileName);
                message.success(intl.formatMessage({
                    id: "user-guide-downloadSuccess",
                    defaultMessage: "Document downloaded Successfully"
                }));
            })
            .catch((err) => {
                isLoading(false);
                message.error(intl.formatMessage({
                    id: "user-guide-downloadFailure",
                    defaultMessage: "Document Download failed"
                }));
                console.error(err);
            })
            .finally(() => {
                isLoading(false);
            });
    };

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 20px',
                margin: '6px 0',
                borderRadius: '8px',
                background: hovered ? '#f0f7ff' : '#fafafa',
                border: `1px solid ${hovered ? '#91caff' : '#e8e8e8'}`,
                transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
                boxShadow: hovered ? '0 2px 8px rgba(24,144,255,0.12)' : '0 1px 3px rgba(0,0,0,0.04)',
            }}
        >
            <FilePdfOutlined style={{ fontSize: '22px', color: '#cf1322', marginRight: '16px', flexShrink: 0 }} />

            <Text
                strong={true}
                style={{
                    flex: 1,
                    fontSize: '14px',
                    color: '#333',
                    lineHeight: '1.4',
                    marginRight: '16px',
                }}
            >
                {intl.formatMessage({ id: el.userGuideName })}
            </Text>

            <Space size="middle" style={{ flexShrink: 0 }}>
                <Select
                    style={{ width: '82px' }}
                    size="small"
                    value={selectedLanguage}
                    options={(el.details as unknown as UserGuideDetail[]).map((detail: UserGuideDetail) => ({
                        label: detail.language.toUpperCase(),
                        value: detail.language,
                    }))}
                    onChange={setSelectedLanguage}
                />
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    size="small"
                    onClick={() => handleDownload(el.details as unknown as UserGuideDetail[])}
                >
                    {intl.formatMessage({ id: "info-portal-download" }).toUpperCase()}
                </Button>
            </Space>
        </div>
    );
}

const HelpFaqUserGuides: React.FunctionComponent<IAppComponentProps> = (props) => {
    const intl = useIntl();
    const [loading, isLoading] = useState<boolean>(false);
    const [userGuidesDocList, setUserGuidesDocList] = useState<getUserGuides_getUserGuidesDocList[]>([]);

    useEffect(() => {
        isLoading(true);
        getUserGuidesDocs()
            .then((data) => {
                setUserGuidesDocList(data.getUserGuidesDocList);
            })
            .catch((err) => {
                message.error(intl.formatMessage({
                    id: "user-guides-documentLoadFailure",
                    defaultMessage: "Unable to load User Guides document list"
                }))
            }).finally(()=> isLoading(false));
    }, []);

    const userGuideGroups = userGuidesDocList.reduce((groups, guide) => {
        if (!groups[guide.userGuideGroupId]) {
            groups[guide.userGuideGroupId] = {groupName: guide.groupName, groupSortOrder: guide.groupSortOrder};
        }
        return groups;
    }, {});


    const sortedUserGuideGroups = Object.entries(userGuideGroups).sort((arg1,arg2)=> {
        // @ts-ignore
        return arg1[1].groupSortOrder - arg2[1].groupSortOrder
    });

    function extractName(item:any) {
        return `${item.groupName}`.trim();
    }

    function extractID(item:any) {
        return item.groupSortOrder;
    }

    const items = sortedUserGuideGroups.map(([groupId, groupObject]) => ({
        key: extractID(groupObject),
        label: props.intl.formatMessage({ id: extractName(groupObject) }),
        children: (
            <div style={{ paddingTop: '8px' }}>
                {userGuidesDocList
                    .filter(el => el.userGuideGroupId === parseInt(groupId))
                    .map((el, idx) => (
                        <UserGuideItem
                            key={`user-guide-id-${idx}`}
                            el={el}
                            intl={intl}
                            isLoading={isLoading}
                        />
                    ))}
            </div>
        ),
    }));

    return (
        <ConfigProvider locale={localStorage.getItem('locale') === 'en' ? enUS : deDe}>
            <Spin size={'large'} spinning={loading}>
                <div style={{ fontFamily: 'BMW Group Condensed' }}>
                    <PageContainer
                        title={false}
                        content={''}
                        extraContent={''}
                        className="site-page-header"
                        style={{ textTransform: 'capitalize' }}
                    >
                        <Title level={4} style={{ marginBottom: '4px' }}>
                            {getI18n('help-document-download', 'Help', intl)}
                        </Title>

                        <h5>
                            {getI18n(
                                'help-document-download-instructions',
                                'Select the language of the document and click on "DOWNLOAD":',
                                intl
                            )}
                        </h5>

                        <Tabs defaultActiveKey="1" items={items} />
                    </PageContainer>
                </div>
            </Spin>
        </ConfigProvider>
    );

};

export default HelpFaqUserGuides;


